import * as Sentry from '@sentry/electron';
import { SpanStatus } from '@sentry/tracing';

import { PRINTER_EVENTS } from '~/main/namespace';
import { PRINTER_FORMATS, PRINTER_KINDS } from '~/main/printer/types';
import { logger, LoggerScope } from '~/main/utils/logger';
import { isSentryReportingEnabled } from '~/root/tools/utils/Sentry';

import { useEventListener } from '.';
import { PrintEventData } from './use-event-listener';

export function useTracePrintExecution() {
  useEventListener(PRINTER_EVENTS.PRINT_COMPLETED, (_, data) => {
    if (!isSentryReportingEnabled) return;
    if (data.format === PRINTER_FORMATS.COMMAND) return;

    if (data.kind === PRINTER_KINDS.RECEIPT) {
      traceReceiptTransaction(data);
    } else if (data.kind === PRINTER_KINDS.LABEL) {
      traceLabelTransaction(data);
    }
  });
}

function traceLabelTransaction(data: PrintEventData) {
  const {
    RETAIL_PRINT_START,
    RETAIL_ZPL_GENERATED,
    HUB_RECEIVED_DATA,
    HUB_MAIN_RECEIVED_DATA,
    HUB_PRINT_COMPLETED,
  } = data.executionTimes;
  if (
    !HUB_RECEIVED_DATA ||
    !HUB_MAIN_RECEIVED_DATA ||
    !HUB_PRINT_COMPLETED ||
    !RETAIL_PRINT_START
  ) {
    logger({ scope: LoggerScope.TIME }).error({
      err: new Error('Data missing from label printing execution times'),
      executionTimes: data.executionTimes,
    });
    return;
  }

  const transaction = Sentry.getCurrentHub().startTransaction({
    name: 'Print Label',
    status: SpanStatus.Ok,
    startTimestamp: RETAIL_PRINT_START / 1000,
  });

  if (RETAIL_ZPL_GENERATED) {
    transaction.startChild({
      op: 'Loading zpl',
      startTimestamp: RETAIL_PRINT_START / 1000,
      endTimestamp: RETAIL_ZPL_GENERATED / 1000,
    });

    transaction.startChild({
      op: 'Transfer data',
      startTimestamp: RETAIL_ZPL_GENERATED / 1000,
      endTimestamp: HUB_RECEIVED_DATA / 1000,
    });
  } else {
    transaction.startChild({
      op: 'Transfer data',
      startTimestamp: RETAIL_PRINT_START / 1000,
      endTimestamp: HUB_RECEIVED_DATA / 1000,
    });
  }

  transaction.startChild({
    op: 'Send to device',
    startTimestamp: HUB_MAIN_RECEIVED_DATA / 1000,
    endTimestamp: HUB_PRINT_COMPLETED / 1000,
  });

  transaction.finish(HUB_PRINT_COMPLETED / 1000);
}

function traceReceiptTransaction(data: PrintEventData) {
  const {
    RETAIL_PRINT_START,
    RETAIL_RECEIPT_LOADED,
    RETAIL_HTML_TRANSFORMED,
    HUB_RECEIVED_DATA,
    HUB_TRANSFORM_HTML_IMAGES,
    HUB_PREPARE_PREVIEW,
    HUB_PREVIEW_IFRAME_LOADED,
    HUB_MAIN_RECEIVED_DATA,
    HUB_OFFSCREEN_RENDERED_DOM_READY,
    HUB_OFFSCREEN_RENDERED_PAINT1,
    HUB_OFFSCREEN_RENDERED_PAINT2,
    HUB_OFFSCREEN_RENDERED_PAINT3,
    HUB_OFFSCREEN_RENDERED,
    HUB_PRINT_COMPLETED,
  } = data.executionTimes;
  if (
    !RETAIL_PRINT_START ||
    !RETAIL_RECEIPT_LOADED ||
    !RETAIL_HTML_TRANSFORMED ||
    !HUB_RECEIVED_DATA ||
    !HUB_TRANSFORM_HTML_IMAGES ||
    !HUB_PREPARE_PREVIEW ||
    !HUB_PREVIEW_IFRAME_LOADED ||
    !HUB_MAIN_RECEIVED_DATA ||
    !HUB_OFFSCREEN_RENDERED_DOM_READY ||
    !HUB_OFFSCREEN_RENDERED ||
    !HUB_PRINT_COMPLETED
  ) {
    logger({ scope: LoggerScope.TIME }).error({
      err: new Error('Data missing from receipt printing execution times'),
      executionTimes: data.executionTimes,
    });
    return;
  }

  const transaction = Sentry.getCurrentHub().startTransaction({
    name: 'Print Receipt',
    status: SpanStatus.Ok,
    startTimestamp: RETAIL_PRINT_START / 1000,
  });

  transaction.startChild({
    op: 'Transforming receipt',
    startTimestamp: RETAIL_RECEIPT_LOADED / 1000,
    endTimestamp: RETAIL_HTML_TRANSFORMED / 1000,
  });

  transaction?.startChild({
    op: 'Transfer data',
    startTimestamp: RETAIL_HTML_TRANSFORMED / 1000,
    endTimestamp: HUB_RECEIVED_DATA / 1000,
  });

  transaction?.startChild({
    op: 'Transform HTML images',
    startTimestamp: HUB_RECEIVED_DATA / 1000,
    endTimestamp: HUB_TRANSFORM_HTML_IMAGES / 1000,
  });

  transaction?.startChild({
    op: 'Create iframe',
    startTimestamp: HUB_PREPARE_PREVIEW / 1000,
    endTimestamp: HUB_PREVIEW_IFRAME_LOADED / 1000,
  });

  transaction?.startChild({
    op: 'Transfer receipt to main process',
    startTimestamp: HUB_PREVIEW_IFRAME_LOADED / 1000,
    endTimestamp: HUB_MAIN_RECEIVED_DATA / 1000,
  });

  const granularReceiptPaint = transaction?.startChild({
    op: 'Render receipt',
    startTimestamp: HUB_MAIN_RECEIVED_DATA / 1000,
    endTimestamp: HUB_OFFSCREEN_RENDERED / 1000,
  });

  granularReceiptPaint?.startChild({
    op: 'Dom ready',
    startTimestamp: HUB_MAIN_RECEIVED_DATA / 1000,
    endTimestamp: HUB_OFFSCREEN_RENDERED_DOM_READY / 1000,
  });

  if (HUB_OFFSCREEN_RENDERED_PAINT1) {
    granularReceiptPaint?.startChild({
      op: 'First paint',
      startTimestamp: HUB_OFFSCREEN_RENDERED_DOM_READY / 1000,
      endTimestamp: HUB_OFFSCREEN_RENDERED_PAINT1 / 1000,
    });
  }
  if (HUB_OFFSCREEN_RENDERED_PAINT1 && HUB_OFFSCREEN_RENDERED_PAINT2) {
    granularReceiptPaint?.startChild({
      op: 'Second paint',
      startTimestamp: HUB_OFFSCREEN_RENDERED_PAINT1 / 1000,
      endTimestamp: HUB_OFFSCREEN_RENDERED_PAINT2 / 1000,
    });
  }
  if (HUB_OFFSCREEN_RENDERED_PAINT2 && HUB_OFFSCREEN_RENDERED_PAINT3) {
    granularReceiptPaint?.startChild({
      op: 'Third paint',
      startTimestamp: HUB_OFFSCREEN_RENDERED_PAINT2 / 1000,
      endTimestamp: HUB_OFFSCREEN_RENDERED_PAINT3 / 1000,
    });
  }

  transaction?.startChild({
    op: 'Send to device',
    startTimestamp: HUB_OFFSCREEN_RENDERED / 1000,
    endTimestamp: HUB_PRINT_COMPLETED / 1000,
  });

  transaction.finish(HUB_PRINT_COMPLETED / 1000);
}
